import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { trackButtonClick, trackModuleView } from '../services/analyticsService';
import {
  FaCode,
  FaTimes,
  FaCheck,
  FaPhone,
  FaEnvelope
} from 'react-icons/fa';
import { doc, getDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { getAccountById } from '../services/firestoreService';
import { createQuestion } from '../services/firebase/questions';
import { getInternalUsers } from '../services/userService';
import { useSuccessManagerQuestionAssignee } from '../hooks/useSuccessManagerQuestionAssignee';
import Stepper, { Step } from '../components/Stepper';
import PortalBackground from '../components/PortalBackground';
import StartQuestionModal from '../components/start/StartQuestionModal';
import { startSidebarLink } from '../components/start/startPageTokens';
import {
  STEP_LABELS,
  StartStepIndicatorColumn,
  StartTaskActionTile,
  StartTaskStatusTile,
  StartTaskStepContent
} from '../components/start/startDashboardTaskUI';
import { getStartFreePageUrl } from '../utils/startFreePageRoutes';
import { getStartFreePageLabel } from '../data/startFreePages';

const DEFAULT_SUCCESS_MANAGER_EMAIL = 'ianr@buildersbreakthrough.com';

// Stepper positions after Claim Free Resource moved out of the flow and into the sidebar cards.
const TOTAL_STEPS = 4;
// `startFlowProgress.completedSteps` keeps its original task IDs (4 = Schedule Call) because
// other pages (Calendly handler, RoadmapBuilder, ProgramCall, NDA return) write the same IDs.
const SCHEDULE_CALL_TASK_ID = 4;
// Marks a saved currentStep as using the 4-step numbering, so legacy 5-step values get shifted once.
const STEP_SCHEME = 'v2-no-free-resource';

const StartDashboard = () => {
  const { darkMode } = useTheme();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check if we're in development mode
  const isDevelopment = process.env.NODE_ENV === 'development';
  
  // State to track completed steps (legacy - kept for backward compatibility)
  const [completedSteps, setCompletedSteps] = useState(new Set());
  
  // State to track checkout completion
  const [checkoutCompleted, setCheckoutCompleted] = useState(false);
  
  // State to track contract signing completion
  const [contractSigned, setContractSigned] = useState(false);
  
  // State to store program and contract data
  const [programData, setProgramData] = useState(null);
  
  // State to store account ID for assessment analysis
  const [accountId, setAccountId] = useState(null);
  
  // State to store user's first name
  const [userFirstName, setUserFirstName] = useState(null);
  
  // State to store user's profile image
  const [userProfileImage, setUserProfileImage] = useState(null);
  
  // State to store existing conditions progress
  const [existingConditionsProgress, setExistingConditionsProgress] = useState(0);
  
  // State to track NDA completion
  const [ndaSigned, setNdaSigned] = useState(false);
  
  // State to store free page
  const [freePage, setFreePage] = useState(null);
  
  // State to track free resource claim completion
  const [freeResourceClaimed, setFreeResourceClaimed] = useState(false);
  
  // State for assessment status from account (keys off AssessmentAnalysis.js: 'in_review' | 'complete')
  const [assessmentStatus, setAssessmentStatus] = useState(null);
  
  // State for success manager - hardcoded for now
  const [successManager] = useState({
    name: 'Ian Riddle',
    firstName: 'Ian',
    lastName: 'Riddle',
    email: 'ianr@buildersbreakthrough.com',
    phone: '419-690-9665',
    profileImage: '/riddleshot.png'
  });
  
  // State for stepper
  const [currentStep, setCurrentStep] = useState(1);
  
  // Ref to track if we're in initial load (to prevent saving during load)
  const isInitialLoad = useRef(true);
  
  // Ref to track if we have a saved step from Firebase (so we don't override it)
  const hasSavedStep = useRef(false);

  const SCHEDULE_ANALYSIS_CALL_URL =
    'https://calendly.com/d/cvgm-g84-c86/assessment-review-next-steps?primary_color=ff2900';

  // State for question modal
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [questionText, setQuestionText] = useState('');
  const [isSubmittingQuestion, setIsSubmittingQuestion] = useState(false);
  const [defaultSmAssignee, setDefaultSmAssignee] = useState(null);
  const { assignee: accountSmAssignee, loading: accountSmAssigneeLoading } =
    useSuccessManagerQuestionAssignee(currentUser, accountId);

  // Dev mode state (stored in localStorage) - only available in development
  const [devMode, setDevMode] = useState(() => {
    if (!isDevelopment) return false;
    return localStorage.getItem('startDashboardDevMode') === 'true';
  });
  
  // Dev mode panel visibility
  const [showDevPanel, setShowDevPanel] = useState(false);
  
  // Toggle dev mode
  const toggleDevMode = () => {
    const newDevMode = !devMode;
    setDevMode(newDevMode);
    localStorage.setItem('startDashboardDevMode', newDevMode.toString());
    if (!newDevMode) {
      setShowDevPanel(false);
    }
  };
  
  
  // Dev mode: Complete all steps
  const completeAllSteps = () => {
    setCompletedSteps(new Set([1, 2, 3, 4, 5, 6, 7]));
  };
  
  // Dev mode: Clear all steps
  const clearAllSteps = () => {
    setCompletedSteps(new Set());
  };
  
  // Dev mode: Toggle checkout completion
  // Note: Checkout is only completed when contract is signed
  const toggleCheckoutCompletion = () => {
    if (checkoutCompleted) {
      // Unchecking checkout also unsigns contract
      setCheckoutCompleted(false);
      setContractSigned(false);
    } else {
      // Checking checkout also signs contract
      setCompletedSteps(new Set([1, 2, 3, 4, 5, 6, 7]));
      setCheckoutCompleted(true);
      setContractSigned(true);
    }
  };
  
  // Dev mode: Toggle contract signing
  const toggleContractSigning = () => {
    const newContractSigned = !contractSigned;
    setContractSigned(newContractSigned);
    if (newContractSigned) {
      // When contract is signed, all steps are completed and checkout is completed
      setCompletedSteps(new Set([1, 2, 3, 4, 5, 6, 7]));
      setCheckoutCompleted(true);
    } else {
      // When contract is unsigned, checkout is also not completed
      setCheckoutCompleted(false);
    }
  };
  
  // Dev mode: Reset everything
  const resetAll = () => {
    setCompletedSteps(new Set());
    setCheckoutCompleted(false);
    setContractSigned(false);
    setProgramData(null);
    setFreeResourceClaimed(false);
  };
  
  // Function to check if a step is completed (for progress display)
  const isStepCompleted = (stepNumber) => {
    return completedSteps.has(stepNumber);
  };
  
  // Load step completion state from Firebase
  useEffect(() => {
    const loadStepProgress = async () => {
      if (currentUser) {
        try {
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            console.log('Loading user data from Firebase:', userData.startFlowProgress);
            
            // Load user's first name
            if (userData.firstName) {
              setUserFirstName(userData.firstName);
            } else if (currentUser.displayName) {
              setUserFirstName(currentUser.displayName.split(' ')[0]);
            } else if (currentUser.email) {
              setUserFirstName(currentUser.email.split('@')[0]);
            }
            
            // Load user's profile image
            const profileImage = userData.profileImage || currentUser.photoURL || null;
            setUserProfileImage(profileImage);
            
            // Load accountId for assessment analysis
            if (userData.accountId) {
              setAccountId(userData.accountId);
              console.log('Loaded accountId from Firebase:', userData.accountId);
              
              // Load freePage and assessmentStatus from account data
              try {
                const accountData = await getAccountById(userData.accountId);
                if (accountData) {
                  if (accountData.freePage) {
                    setFreePage(accountData.freePage);
                    console.log('Loaded freePage from account:', accountData.freePage);
                  }
                  if (accountData.assessmentStatus === 'complete' || accountData.assessmentStatus === 'in_review') {
                    setAssessmentStatus(accountData.assessmentStatus);
                  }
                }
              } catch (error) {
                console.error('Error loading account data for freePage:', error);
              }
            }
            
            // Load existing conditions progress
            if (userData.businessAssessmentProgress?.progress !== undefined) {
              setExistingConditionsProgress(userData.businessAssessmentProgress.progress);
              console.log('Loaded existing conditions progress from Firebase:', userData.businessAssessmentProgress.progress);
            }
            
            // Load NDA signing status
            if (userData.signedNDA || (userData.collection && userData.collection.some(item => item.isSignedNDA))) {
              setNdaSigned(true);
              console.log('NDA is signed');
            }
            
            // Load free resource claim status
            if (userData.startFlowProgress?.freeResourceClaimed) {
              setFreeResourceClaimed(true);
              console.log('Free resource claimed');
            }
            
            // Always try to find account by email as well (in case accountId is not set)
            // Note: accountId state might not be updated yet, so check userData.accountId too
            if (!userData.accountId && currentUser.email) {
              try {
                const normalizedEmail = currentUser.email.toLowerCase().trim();
                
                // Try multiple strategies to find the account
                const searchStrategies = [
                  // Strategy 1: Find by contact.email with type filter
                  query(
                    collection(db, 'accounts'),
                    where('contact.email', '==', normalizedEmail),
                    where('type', '==', 'Leads')
                  ),
                  // Strategy 2: Find by direct email with type filter
                  query(
                    collection(db, 'accounts'),
                    where('email', '==', normalizedEmail),
                    where('type', '==', 'Leads')
                  ),
                  // Strategy 3: Find by contact.email without type filter
                  query(
                    collection(db, 'accounts'),
                    where('contact.email', '==', normalizedEmail)
                  ),
                  // Strategy 4: Find by direct email without type filter
                  query(
                    collection(db, 'accounts'),
                    where('email', '==', normalizedEmail)
                  )
                ];
                
                for (const searchQuery of searchStrategies) {
                  try {
                    const accountsSnapshot = await getDocs(searchQuery);
                    if (!accountsSnapshot.empty) {
                      const leadAccountId = accountsSnapshot.docs[0].id;
                      setAccountId(leadAccountId);
                      console.log('Found accountId by email using strategy:', searchStrategies.indexOf(searchQuery) + 1, leadAccountId);
                      
                      // Load freePage and assessmentStatus from account data
                      try {
                        const accountData = await getAccountById(leadAccountId);
                        if (accountData) {
                          if (accountData.freePage) {
                            setFreePage(accountData.freePage);
                            console.log('Loaded freePage from account:', accountData.freePage);
                          }
                          if (accountData.assessmentStatus === 'complete' || accountData.assessmentStatus === 'in_review') {
                            setAssessmentStatus(accountData.assessmentStatus);
                          }
                        }
                      } catch (error) {
                        console.error('Error loading account data for freePage:', error);
                      }
                      
                      break; // Stop searching once we find one
                    }
                  } catch (queryError) {
                    console.warn('Query strategy failed:', queryError);
                    // Continue to next strategy
                  }
                }
              } catch (error) {
                console.error('Error finding account by email:', error);
              }
            }
            
            if (userData.startFlowProgress) {
              // Load saved current step if available. Steps saved before the Claim Free Resource
              // step was removed used 1-5 numbering, so shift those down one.
              if (userData.startFlowProgress.currentStep) {
                const savedStep =
                  userData.startFlowProgress.stepScheme === STEP_SCHEME
                    ? userData.startFlowProgress.currentStep
                    : Math.max(1, userData.startFlowProgress.currentStep - 1);
                setCurrentStep(Math.min(savedStep, TOTAL_STEPS));
                hasSavedStep.current = true; // Mark that we have a saved step
                console.log('Loaded currentStep from Firebase:', userData.startFlowProgress.currentStep);
              }
              
              // If contract is signed, steps completed; checkoutCompleted only after payment
              if (userData.startFlowProgress.contractSigned) {
                setCompletedSteps(new Set([1, 2, 3, 4, 5, 6, 7]));
                setContractSigned(true);
                setCheckoutCompleted(!!userData.startFlowProgress.checkoutCompleted);
                if (userData.startFlowProgress.programData) {
                  setProgramData(userData.startFlowProgress.programData);
                }
              }
              if (userData.startFlowProgress.checkoutCompleted) {
                setCheckoutCompleted(true);
              }
              if (!userData.startFlowProgress.contractSigned) {
                // Only load individual step data if contract is not signed
                if (userData.startFlowProgress.completedSteps) {
                  setCompletedSteps(new Set(userData.startFlowProgress.completedSteps));
                  console.log('Loaded completedSteps from Firebase:', userData.startFlowProgress.completedSteps);
                }
                // checkoutCompleted is only true when payment is complete
                // Load program data if available (for future contract signing)
                if (userData.startFlowProgress.programData) {
                  setProgramData(userData.startFlowProgress.programData);
                }
              }
            } else {
              // If user document doesn't exist, still try to load profile image from currentUser
              const profileImage = currentUser.photoURL || null;
              setUserProfileImage(profileImage);
            }
          }
        } catch (error) {
          console.error('Error loading step progress:', error);
          // On error, still try to load profile image from currentUser
          if (currentUser?.photoURL) {
            setUserProfileImage(currentUser.photoURL);
          }
        }
      }
    };

    // Always load from Firebase on component mount, but navigation state takes priority
    loadStepProgress().then(() => {
      // Mark initial load as complete after loading
      isInitialLoad.current = false;
    });
  }, [currentUser]);

  // Check for checkout completion from navigation state
  // Note: Checkout completion is now only set when contract is signed
  // This effect is kept for backward compatibility but doesn't set checkoutCompleted
  useEffect(() => {
    if (location.state?.checkoutCompleted && !location.state?.contractSigned) {
      console.log('Checkout completed (signup only), location state:', location.state);
      // Don't set checkoutCompleted - only contract signing completes checkout
      // Save program data if provided (for future contract signing)
      if (location.state?.program) {
        console.log('Setting program data:', location.state.program);
        setProgramData(location.state.program);
      }
    }
  }, [location.state, currentUser]);

  // Check for contract signing completion from navigation state (contract only - no checkoutCompleted)
  useEffect(() => {
    if (location.state?.contractSigned && !location.state?.paymentCompleted) {
      console.log('Contract signed, location state:', location.state);
      setContractSigned(true);
      
      // When contract is signed, steps completed but checkout not until payment
      setCompletedSteps(new Set([1, 2, 3, 4, 5, 6, 7]));
      
      // Save program data if provided
      if (location.state?.program) {
        setProgramData(location.state.program);
      }
      if (currentUser) {
        updateContractSigningCompletion(location.state?.program);
      }
    }
  }, [location.state, currentUser]);

  // Check for payment completion from navigation state (welcome message shows after payment)
  useEffect(() => {
    if (location.state?.paymentCompleted || location.state?.checkoutCompleted) {
      setCheckoutCompleted(true);
      setContractSigned(true);
      if (location.state?.program) {
        setProgramData(location.state.program);
      }
    }
  }, [location.state]);

  // Lead → client conversion is NOT done here. It used to run off startFlowProgress flags, which
  // the browser sets and can therefore be wrong. It now happens server-side once Stripe confirms
  // the checkout session actually completed (functions/stripe/leadConversion.js), driven by both
  // the checkout.session.completed webhook and the verifyCheckoutSession call on the return page.

  // Note: Step completion is now handled directly in the source pages (e.g. RoadmapBuilder)
  // No need for navigation state handling
  

  // Function to update checkout completion
  const updateCheckoutCompletion = async (programDataToSave = null) => {
    if (currentUser) {
      try {
        const updateData = {
          startFlowProgress: {
            completedSteps: Array.from(completedSteps),
            checkoutCompleted: true,
            currentStep: currentStep, // Preserve current step
            lastUpdated: new Date().toISOString()
          },
          lastUpdated: new Date().toISOString()
        };
        
        // Include program data if available (use parameter or state)
        const dataToSave = programDataToSave || programData;
        if (dataToSave) {
          updateData.startFlowProgress.programData = dataToSave;
          console.log('Saving program data to Firebase:', dataToSave);
        } else {
          console.log('No program data to save');
        }
        
        await updateDoc(doc(db, 'users', currentUser.uid), updateData);
        console.log('Checkout completion saved to Firebase successfully');
      } catch (error) {
        console.error('Error saving checkout completion:', error);
      }
    }
  };

  const markFreeResourceClaimed = async () => {
    if (!currentUser) return;
    try {
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      const existing = userDoc.exists() ? userDoc.data()?.startFlowProgress || {} : {};
      await updateDoc(doc(db, 'users', currentUser.uid), {
        startFlowProgress: {
          ...existing,
          freeResourceClaimed: true,
          lastUpdated: new Date().toISOString()
        },
        lastUpdated: new Date().toISOString()
      });
      setFreeResourceClaimed(true);
    } catch (error) {
      console.error('Error marking free resource claimed:', error);
    }
  };

  // Function to save current step to Firebase
  const saveCurrentStep = async (step) => {
    if (currentUser) {
      try {
        // Get existing data to preserve it
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        const existing = userDoc.exists() && userDoc.data().startFlowProgress 
          ? userDoc.data().startFlowProgress 
          : {};
        
        const updateData = {
          startFlowProgress: {
            ...existing,
            currentStep: step,
            stepScheme: STEP_SCHEME,
            lastUpdated: new Date().toISOString()
          },
          lastUpdated: new Date().toISOString()
        };
        
        await updateDoc(doc(db, 'users', currentUser.uid), updateData);
        console.log('Current step saved to Firebase:', step);
      } catch (error) {
        console.error('Error saving current step:', error);
      }
    }
  };

  // Function to update contract signing completion (checkoutCompleted only after payment)
  const updateContractSigningCompletion = async (programDataToSave = null) => {
    if (currentUser) {
      try {
        const allStepsCompleted = [1, 2, 3, 4, 5, 6, 7];
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        const existing = userDoc.exists() ? userDoc.data()?.startFlowProgress || {} : {};
        
        const updateData = {
          startFlowProgress: {
            ...existing,
            completedSteps: allStepsCompleted,
            contractSigned: true,
            currentStep: currentStep,
            stepScheme: STEP_SCHEME,
            lastUpdated: new Date().toISOString()
          },
          lastUpdated: new Date().toISOString()
        };
        
        const dataToSave = programDataToSave || programData;
        if (dataToSave) {
          updateData.startFlowProgress.programData = dataToSave;
        }
        
        await updateDoc(doc(db, 'users', currentUser.uid), updateData);
        
        setCompletedSteps(new Set(allStepsCompleted));
        setContractSigned(true);
        if (dataToSave) {
          setProgramData(dataToSave);
        }
      } catch (error) {
        console.error('Error saving contract signing completion:', error);
      }
    }
  };

  // Determine current step based on progress
  useEffect(() => {
    let newStep;

    // Priority order: contract > program > schedule call completed > assessment+NDA > assessment only > default
    if (contractSigned) {
      newStep = 4; // All steps completed
    } else if (programData) {
      newStep = 4; // Program chosen, show Choose Program step
    } else if (completedSteps.has(SCHEDULE_CALL_TASK_ID)) {
      // Schedule Call completed, move to Choose Program (step 4)
      newStep = 4;
    } else if (existingConditionsProgress >= 99 && ndaSigned) {
      // Assessment and NDA both done, move to Schedule Call (step 3)
      newStep = 3;
    } else if (existingConditionsProgress >= 99) {
      // Assessment done (100% complete), ALWAYS move to NDA (step 2)
      newStep = 2;
    } else {
      newStep = 1; // Business Analysis
    }
    
    // During initial load, only override saved step if assessment is 100% complete
    if (isInitialLoad.current && hasSavedStep.current) {
      // If assessment is complete, always advance to step 2
      if (existingConditionsProgress >= 99) {
        setCurrentStep(newStep);
        saveCurrentStep(newStep);
      }
      return;
    }
    
    setCurrentStep(newStep);
    
    // Save step to Firebase only if not initial load
    if (!isInitialLoad.current) {
      saveCurrentStep(newStep);
    }
  }, [completedSteps, contractSigned, ndaSigned, existingConditionsProgress, programData, accountId]);

  // Load Calendly widget script and styles
  useEffect(() => {
    // Load CSS
    if (!document.getElementById('calendly-widget-css')) {
      const link = document.createElement('link');
      link.id = 'calendly-widget-css';
      link.rel = 'stylesheet';
      link.href = 'https://assets.calendly.com/assets/external/widget.css';
      document.head.appendChild(link);
    }
    
    // Load JS (same pattern as PortalOperations.js)
    if (!document.getElementById('calendly-widget-js')) {
      const script = document.createElement('script');
      script.id = 'calendly-widget-js';
      script.src = 'https://assets.calendly.com/assets/external/widget.js';
      script.async = true;
      document.head.appendChild(script);
    }
  }, []);

  // Resolve default Success Manager assignee (Ian) when account has none
  useEffect(() => {
    if (!showQuestionModal || accountSmAssignee?.id || defaultSmAssignee != null) return;

    let cancelled = false;
    const resolveDefault = async () => {
      try {
        const users = await getInternalUsers();
        if (cancelled) return;
        const smUser = users.find(
          (u) => (u.email || '').toLowerCase() === DEFAULT_SUCCESS_MANAGER_EMAIL
        );
        setDefaultSmAssignee({
          id: smUser?.id || null,
          name: successManager.name || smUser?.name || 'Success Manager'
        });
      } catch (error) {
        console.error('Error resolving default Success Manager assignee:', error);
        if (!cancelled) {
          setDefaultSmAssignee({ id: null, name: successManager.name || 'Success Manager' });
        }
      }
    };
    resolveDefault();
    return () => {
      cancelled = true;
    };
  }, [showQuestionModal, accountSmAssignee?.id, defaultSmAssignee, successManager.name]);

  const questionAssignee = accountSmAssignee ?? defaultSmAssignee;
  const questionModalLoading =
    showQuestionModal &&
    (accountSmAssigneeLoading || (questionAssignee == null && defaultSmAssignee == null));

  // Listen for Calendly event scheduled to mark step 4 as complete
  useEffect(() => {
    const handleCalendlyEvent = async (e) => {
      // Check if this is a Calendly event_scheduled message
      if (e.data && e.data.event === 'calendly.event_scheduled') {
        console.log('Calendly event scheduled detected:', e.data);
        
        if (currentUser) {
          try {
            // Get existing user data to preserve all startFlowProgress data
            const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
            let existingCompletedSteps = [];
            let existingFlowProgress = {};
            
            if (userDoc.exists()) {
              const userData = userDoc.data();
              if (userData.startFlowProgress) {
                existingFlowProgress = userData.startFlowProgress;
                if (userData.startFlowProgress.completedSteps) {
                  existingCompletedSteps = userData.startFlowProgress.completedSteps;
                }
              }
            }
            
            // Add step 4 (Schedule Call) if not already completed
            if (!existingCompletedSteps.includes(4)) {
              existingCompletedSteps.push(4);
              
              // Update Firebase - preserve all existing startFlowProgress data
              await updateDoc(doc(db, 'users', currentUser.uid), {
                startFlowProgress: {
                  ...existingFlowProgress,
                  completedSteps: existingCompletedSteps,
                  currentStep: existingFlowProgress.currentStep || currentStep, // Preserve current step
                  // Exact scheduling time, so the CRM lead timeline can date this action
                  analysisCallScheduledAt:
                    existingFlowProgress.analysisCallScheduledAt || new Date().toISOString(),
                  lastUpdated: new Date().toISOString()
                },
                lastUpdated: new Date().toISOString()
              });
              
              // Update local state
              setCompletedSteps(new Set(existingCompletedSteps));
              
              // Force a re-render by updating currentStep if needed
              // The step determination useEffect will handle the proper step
              
              console.log('Step 4 (Schedule Call) marked as complete');
            }
          } catch (error) {
            console.error('Error completing step 4 (Schedule Call):', error);
          }
        }
      }
    };

    // Add event listener for Calendly messages
    window.addEventListener('message', handleCalendlyEvent);

    return () => {
      window.removeEventListener('message', handleCalendlyEvent);
    };
  }, [currentUser]);

  /** Same pattern as PortalOperations.js Schedule a Strategy Call — Calendly popup widget */
  const openScheduleAnalysisCallPopup = async () => {
    if (completedSteps.has(SCHEDULE_CALL_TASK_ID)) return;

    let profile = null;
    if (currentUser) {
      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) profile = userDoc.data();
      } catch (error) {
        console.error('Error loading user data for Calendly prefill:', error);
      }
    }

    const baseUrl = SCHEDULE_ANALYSIS_CALL_URL;
    const firstName =
      profile?.firstName?.trim() ||
      currentUser?.displayName?.split(/\s+/)[0] ||
      currentUser?.email?.split('@')[0] ||
      '';
    const lastName =
      profile?.lastName?.trim() || (currentUser?.displayName?.split(/\s+/)?.slice(1)?.join(' ') || '') || '';
    const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
    const prefill = currentUser || profile ? {
      firstName,
      lastName,
      name: fullName || currentUser?.displayName || currentUser?.email?.split('@')[0] || '',
      email: currentUser?.email || '',
      phone: profile?.phone?.trim() || ''
    } : {};
    const hasPrefill =
      prefill.name || prefill.firstName || prefill.lastName || prefill.email || prefill.phone;

    const openPopup = () => {
      if (window.Calendly) {
        window.Calendly.initPopupWidget({
          url: baseUrl,
          prefill: hasPrefill ? prefill : undefined
        });
      } else {
        const params = new URLSearchParams();
        if (prefill.name) params.set('name', prefill.name);
        if (prefill.email) params.set('email', prefill.email);
        if (prefill.firstName) params.set('first_name', prefill.firstName);
        if (prefill.lastName) params.set('last_name', prefill.lastName);
        const qs = params.toString();
        window.open(qs ? `${baseUrl}&${qs}` : baseUrl, '_blank');
      }
    };

    if (window.Calendly) {
      openPopup();
    } else {
      let tries = 0;
      const intervalId = setInterval(() => {
        tries += 1;
        if (window.Calendly) {
          clearInterval(intervalId);
          openPopup();
        } else if (tries > 50) {
          clearInterval(intervalId);
          openPopup();
        }
      }, 100);
    }
  };

  // Handle question submission
  const handleQuestionSubmit = async (e) => {
    e.preventDefault();
    if (!questionText.trim()) {
      alert('Please enter your question.');
      return;
    }
    if (!questionAssignee?.id) {
      alert('Unable to send your question right now. Please contact your Success Manager directly.');
      return;
    }
    setIsSubmittingQuestion(true);
    try {
      const questionData = {
        accountId: currentUser.uid,
        accountName: currentUser.displayName || currentUser.email?.split('@')[0] || 'Unknown User',
        assigneeId: questionAssignee.id,
        assigneeName: questionAssignee.name,
        questionText: questionText.trim(),
        department: 'General',
        createdBy: currentUser.uid,
        createdByName: currentUser.displayName || currentUser.email?.split('@')[0] || 'Unknown User'
      };
      await createQuestion(questionData);
      setQuestionText('');
      setShowQuestionModal(false);
    } catch (error) {
      console.error('Error submitting question:', error);
      alert('Failed to submit question. Please try again.');
    } finally {
      setIsSubmittingQuestion(false);
    }
  };

  const getFlowCompletion = () => {
    let total = 0;
    if (existingConditionsProgress >= 99 && accountId) total += 25;
    if (ndaSigned) total += 25;
    if (completedSteps.has(SCHEDULE_CALL_TASK_ID)) total += 25;
    if (programData) total += 25;
    return total;
  };

  const totalCompletion = getFlowCompletion();
  const progressBarColor =
    totalCompletion >= 80 ? 'bg-emerald-500' : totalCompletion >= 40 ? 'bg-amber-500' : 'bg-red-500';

  const completedStepCount = [
    existingConditionsProgress >= 99 && accountId,
    ndaSigned,
    completedSteps.has(SCHEDULE_CALL_TASK_ID),
    !!programData
  ].filter(Boolean).length;

  const cardSurface =
    'rounded-xl border bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 shadow-sm';

  const freeResourceName = getStartFreePageLabel(freePage);

  const openFreeResource = async () => {
    trackButtonClick('Claim Free Resource', 'start_dashboard_cards');
    if (!freeResourceClaimed) {
      await markFreeResourceClaimed();
    }
    const resourceUrl = getStartFreePageUrl(freePage);
    if (resourceUrl) {
      navigate(resourceUrl);
    }
  };

  // Emojis rather than icons, matching the portal nav and department headers.
  const quickLinks = [
    {
      id: 'meet-team',
      emoji: '👋',
      title: 'Meet The Team',
      onClick: () => { trackModuleView('card_meet_team'); navigate('/start/team'); }
    },
    {
      id: 'success-stories',
      emoji: '🏆',
      title: 'Success Stories',
      onClick: () => { trackModuleView('card_success_stories'); navigate('/start/success-stories'); }
    },
    {
      id: 'cqc-home',
      emoji: '🏡',
      title: 'Sift CQC Home',
      onClick: () => { trackModuleView('card_cqc_home'); navigate('/start/cqc-home'); }
    },
    {
      id: 'ask-question',
      emoji: '💬',
      title: 'Ask a Question',
      onClick: () => { trackButtonClick('Ask a Question', 'start_dashboard_cards'); setShowQuestionModal(true); }
    },
    // Only when a free page has been assigned to the account in CRM ▸ Lead Overview.
    ...(freePage
      ? [{
          id: 'free-resource',
          emoji: '🎁',
          title: 'Claim Free Resource',
          subtitle: freeResourceName,
          onClick: () => { void openFreeResource(); }
        }]
      : [])
  ];

  return (
    <div className={`min-h-screen p-6 md:p-8 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
      {/* Same ambient BBA background the portal shows while awaiting the start date */}
      <PortalBackground />
      <div className="relative w-full max-w-7xl mx-auto">

      {/* Centered header */}
      <div className="text-center mb-8">
        <div className="flex justify-center mb-4">
          <img src="/bba-emblem.svg" alt="BBA Logo" className="h-16 md:h-20 w-auto" />
        </div>
        <h1 className={`text-3xl md:text-4xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
          Welcome to BBA,{' '}
          <span className="text-red-600 dark:text-red-400">
            {userFirstName || 'there'}
          </span>
          !
        </h1>
        <p className={`text-sm md:text-base mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          We&apos;re excited you considered partnering with us.
        </p>
      </div>

      {(location.state?.paymentCompleted || location.state?.checkoutCompleted || checkoutCompleted) && (
        <div className="mb-6 p-4 bg-green-100 dark:bg-green-900/30 rounded-lg border border-green-200 dark:border-green-700">
          <p className="text-green-800 dark:text-green-200 font-medium text-sm">
            Contract signed successfully! Welcome to your BBA program.
          </p>
        </div>
      )}

      {/* Dev Mode Toggle Button - Only show in development */}
      {isDevelopment && (
        <div className="fixed top-20 right-4 z-50">
          <button
            onClick={() => {
              if (!devMode) {
                setDevMode(true);
                localStorage.setItem('startDashboardDevMode', 'true');
              }
              setShowDevPanel(!showDevPanel);
            }}
            className={`p-3 rounded-full shadow-lg transition-all ${
              devMode
                ? 'bg-purple-600 hover:bg-purple-700 text-white'
                : 'bg-gray-400 hover:bg-gray-500 text-white'
            }`}
            title="Toggle Dev Mode Panel"
          >
            <FaCode className="text-xl" />
          </button>
        </div>
      )}

      {/* Dev Mode Panel - Only show in development */}
      {isDevelopment && showDevPanel && (
        <div className="fixed top-24 right-4 z-50 w-80 bg-white dark:bg-gray-800 rounded-lg shadow-2xl border-2 border-purple-500 p-6 max-h-[80vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-bold text-purple-600 dark:text-purple-400 flex items-center">
              <FaCode className="mr-2" />
              Dev Mode Controls
            </h3>
            <button
              onClick={() => setShowDevPanel(false)}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <FaTimes />
            </button>
          </div>

          {/* Dev Mode Toggle */}
          <div className="mb-6 p-4 bg-gray-100 dark:bg-gray-700 rounded-lg">
            <div className="flex justify-between items-center">
              <span className="font-medium text-gray-700 dark:text-gray-300">Dev Mode</span>
              <button
                onClick={toggleDevMode}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  devMode ? 'bg-purple-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    devMode ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>


          {/* Checkout Completion */}
          <div className="mb-6">
            <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-3">Sign Up Status</h4>
            <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-gray-700 dark:text-gray-300">Checkout Completed</span>
                <button
                  onClick={toggleCheckoutCompletion}
                  className={`px-3 py-1 rounded text-sm font-medium ${
                    checkoutCompleted
                      ? 'bg-green-600 text-white hover:bg-green-700'
                      : 'bg-gray-300 text-gray-700 hover:bg-gray-400 dark:bg-gray-600 dark:text-gray-200'
                  }`}
                >
                  {checkoutCompleted ? '✓' : '○'}
                </button>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 italic">
                Only completed when contract is signed
              </p>
            </div>
          </div>

          {/* Contract Signing */}
          <div className="mb-6">
            <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-3">Contract Status</h4>
            <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-700 rounded">
              <span className="text-sm text-gray-700 dark:text-gray-300">Contract Signed</span>
              <button
                onClick={toggleContractSigning}
                className={`px-3 py-1 rounded text-sm font-medium ${
                  contractSigned
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'bg-gray-300 text-gray-700 hover:bg-gray-400 dark:bg-gray-600 dark:text-gray-200'
                }`}
              >
                {contractSigned ? '✓' : '○'}
              </button>
            </div>
          </div>

          {/* Existing Conditions Status */}
          <div className="mb-6">
            <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-3">Existing Conditions Status</h4>
            <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-gray-700 dark:text-gray-300">Existing Conditions Page</span>
                <button
                  onClick={() => navigate('/start/existing-conditions')}
                  className="px-3 py-1 rounded text-sm font-medium bg-blue-600 text-white hover:bg-blue-700"
                >
                  Go to Existing Conditions
                </button>
              </div>
            </div>
          </div>

          {/* Step Progress Tracker */}
          <div className="mb-6">
            <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-3">Step Progress</h4>
            <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded space-y-2">
              {[
                { num: 1, name: 'View Business Analysis', completed: existingConditionsProgress >= 99 && accountId },
                { num: 2, name: 'Sign NDA', completed: ndaSigned },
                { num: 3, name: 'Schedule Analysis Call', completed: completedSteps.has(SCHEDULE_CALL_TASK_ID) },
                { num: 4, name: 'Choose a Program', completed: !!programData }
              ].map((step) => {
                return (
                  <div
                    key={step.num}
                    className="flex items-center justify-between p-2 rounded bg-white dark:bg-gray-800"
                  >
                    <div className="flex items-center gap-2">
                      <div
                        className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${
                          step.completed
                            ? 'bg-green-600 text-white'
                            : 'bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-300'
                        }`}
                      >
                        {step.completed ? <FaCheck className="h-3 w-3" /> : step.num}
                      </div>
                      <span className="text-xs text-gray-700 dark:text-gray-300">
                        {step.num}. {step.name}
                      </span>
                    </div>
                    <span
                      className={`text-xs font-medium ${
                        step.completed
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-gray-400 dark:text-gray-500'
                      }`}
                    >
                      {step.completed ? 'Completed' : 'Pending'}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="mb-4">
            <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-3">Quick Actions</h4>
            <button
              onClick={resetAll}
              className="w-full px-4 py-2 bg-red-600 text-white rounded font-medium hover:bg-red-700"
            >
              Reset Everything
            </button>
          </div>

          {/* Current Status Display */}
          <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded border border-blue-200 dark:border-blue-700">
            <h4 className="font-semibold text-blue-800 dark:text-blue-200 mb-2 text-sm">Current Status</h4>
            <div className="text-xs text-blue-700 dark:text-blue-300 space-y-1">
              <p>Business Analysis Viewed: {existingConditionsProgress >= 99 && accountId ? '✓' : '○'}</p>
              <p>NDA Signed: {ndaSigned ? '✓' : '○'}</p>
              <p>Existing Conditions Progress: {Math.round(existingConditionsProgress)}%</p>
              <p>Program Selected: {programData ? '✓' : '○'}</p>
              <p>Contract: {contractSigned ? '✓' : '○'}</p>
              <p>Free Resource Claimed: {freeResourceClaimed ? '✓' : '○'}</p>
              <p>Current Step: {currentStep}/{TOTAL_STEPS}</p>
            </div>
          </div>
        </div>
      )}

      {/* Program info when contract signed */}
      {contractSigned && (
        <div className={`${cardSurface} p-5 mb-6`}>
          <div className="grid md:grid-cols-2 gap-5 text-left">
            <div>
              <h4 className="font-semibold text-gray-800 dark:text-white mb-2">Your Program</h4>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                <span className="font-medium">Program:</span> {programData?.name || 'Selected Program'}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                <span className="font-medium">Start Date:</span>{' '}
                {programData?.startDate
                  ? new Date(programData.startDate).toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })
                  : 'To be determined'}
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-gray-800 dark:text-white mb-2">Portal Access</h4>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                <span className="font-medium">Status:</span>{' '}
                <span className="px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 rounded-full text-xs">
                  Pending Start Date
                </span>
              </p>
            </div>
          </div>
          <p className="text-sm text-blue-800 dark:text-blue-200 mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
            You will gain access to your upgraded portal on your start date.
          </p>
        </div>
      )}

      {/* Main: tasks (3/4) + sidebar (1/4) */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 items-start mb-6">
        <div className="lg:col-span-3">
      <div id="start-dashboard-my-tasks" className={`${cardSurface} p-5`}>
        <div className="mb-4 text-center">
          <h2 className="font-semibold text-gray-800 dark:text-white text-lg">My Tasks</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            Complete the steps below to get started with BBA.
          </p>
        </div>

        <div className="mb-5">
          <div className="flex items-center justify-between gap-3 mb-2">
            <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
              {completedStepCount} of {TOTAL_STEPS} steps complete
            </span>
            <span
              className={`text-sm font-bold px-2.5 py-0.5 rounded-full ${
                darkMode ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-800'
              }`}
            >
              {totalCompletion}%
            </span>
          </div>
          <div className="h-3 rounded-full bg-gray-200 dark:bg-gray-600 overflow-hidden">
            <div
              className={`h-full ${progressBarColor} transition-all duration-500 rounded-full`}
              style={{ width: `${totalCompletion}%` }}
            />
          </div>
        </div>

        <div className="w-full">
          <Stepper
            className="w-full items-stretch justify-start p-0"
            initialStep={currentStep}
            onStepChange={(step) => {
              setCurrentStep(step);
              saveCurrentStep(step);
            }}
            onFinalStepCompleted={async () => {
              console.log('All tasks completed!');
            }}
            backButtonText="Previous"
            nextButtonText="Next"
            stepCircleContainerClassName="!max-w-none border-0 shadow-none bg-transparent"
            contentClassName="min-h-[160px] !px-2"
            stepContainerClassName="px-2 pb-2 flex w-full items-end"
            hideCompleteButton={true}
            renderStepIndicator={({ step, currentStep: stepperStep, onStepClick }) => (
              <StartStepIndicatorColumn
                step={step}
                currentStep={stepperStep}
                label={STEP_LABELS[step - 1]}
                onStepClick={onStepClick}
              />
            )}
          >
                        <Step>
                          <StartTaskStepContent
                            stepNumber={1}
                            title="Business Analysis"
                            subtitle={
                              existingConditionsProgress >= 99 && accountId
                                ? assessmentStatus === 'complete'
                                  ? 'Your personalized business analysis is ready to view.'
                                  : 'Your business analysis is being reviewed by our team.'
                                : 'Complete your business assessment to unlock your personalized business analysis.'
                            }
                            action={
                              existingConditionsProgress >= 99 && accountId ? (
                                assessmentStatus === 'complete' ? (
                                  <StartTaskActionTile
                                    emoji="📊"
                                    label="Click Here to View Analysis"
                                    onClick={() => navigate(`/start/assessment-analysis/${accountId}`)}
                                  />
                                ) : (
                                  <StartTaskStatusTile
                                    emoji="📊"
                                    label="In Review"
                                    variant="warning"
                                  />
                                )
                              ) : (
                                <StartTaskActionTile
                                  emoji="📊"
                                  label="Click Here to Complete Assessment"
                                  onClick={() => {
                                    trackButtonClick('Complete Assessment', 'start_dashboard_stepper');
                                    navigate('/start/existing-conditions');
                                  }}
                                />
                              )
                            }
                          />
                        </Step>
                        <Step>
                          <StartTaskStepContent
                            stepNumber={2}
                            title="Sign NDA"
                            subtitle="Ensure your business information is safe and secure."
                            action={
                              ndaSigned ? (
                                <StartTaskStatusTile
                                  emoji="✍️"
                                  label="Signed"
                                  variant="success"
                                />
                              ) : (
                                <StartTaskActionTile
                                  emoji="✍️"
                                  label="Click Here to Sign NDA"
                                  onClick={() => {
                                    trackButtonClick('Sign NDA', 'start_dashboard_stepper');
                                    navigate('/start/nda');
                                  }}
                                />
                              )
                            }
                          />
                        </Step>
                        <Step>
                          <div id="schedule-analysis-call">
                            <StartTaskStepContent
                              stepNumber={3}
                              title="Schedule Analysis Call"
                              subtitle="Book your call with our team to review your business analysis and discuss proposed implementations."
                              action={
                                completedSteps.has(SCHEDULE_CALL_TASK_ID) ? (
                                  <StartTaskStatusTile
                                    emoji="📅"
                                    label="Scheduled"
                                    variant="success"
                                  />
                                ) : (
                                  <StartTaskActionTile
                                    emoji="📅"
                                    label="Click Here to Schedule Call"
                                    onClick={() => {
                                      trackButtonClick('Schedule Analysis Call', 'start_dashboard_stepper');
                                      void openScheduleAnalysisCallPopup();
                                    }}
                                  />
                                )
                              }
                            />
                          </div>
                        </Step>
                        <Step>
                          <StartTaskStepContent
                            stepNumber={4}
                            title="Choose a Program"
                            subtitle="Select the program that best fits your business needs and goals."
                            action={
                              programData ? (
                                <StartTaskStatusTile
                                  emoji="🎓"
                                  label="Selected"
                                  variant="success"
                                />
                              ) : (
                                <StartTaskActionTile
                                  emoji="🎓"
                                  label="Click Here to Choose Program"
                                  onClick={() => {
                                    trackButtonClick('Choose Program', 'start_dashboard_stepper');
                                    navigate('/start/program');
                                  }}
                                />
                              )
                            }
                          />
                        </Step>
                      </Stepper>
        </div>
      </div>
        </div>

        <aside className="lg:col-span-1 flex flex-col gap-3">
          {successManager && (
            <div className={`${cardSurface} p-4`}>
              {/* Identity row — mirrors the compact Success Manager footer on ClientDashboard */}
              <div className="flex items-center gap-3">
                {successManager.profileImage ? (
                  <img
                    src={successManager.profileImage}
                    alt={successManager.name || 'Success Manager'}
                    className="w-10 h-10 rounded-full object-cover shrink-0"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 flex items-center justify-center text-xs font-semibold shrink-0">
                    {(successManager.name || successManager.firstName || 'SM').charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                    {successManager.name ||
                      `${successManager.firstName || ''} ${successManager.lastName || ''}`.trim()}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Your Success Manager</p>
                </div>
              </div>

              {(successManager.phone || successManager.email) && (
                <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 flex flex-col gap-1.5">
                  {successManager.phone && (
                    <a
                      href={`tel:${successManager.phone}`}
                      className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                    >
                      <FaPhone className="w-3 h-3 shrink-0" aria-hidden />
                      {successManager.phone}
                    </a>
                  )}
                  {successManager.email && (
                    <a
                      href={`mailto:${successManager.email}`}
                      className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400 transition-colors min-w-0"
                    >
                      <FaEnvelope className="w-3 h-3 shrink-0" aria-hidden />
                      <span className="truncate">{successManager.email}</span>
                    </a>
                  )}
                </div>
              )}
            </div>
          )}

          {quickLinks.map((link) => (
            <button
              key={link.id}
              type="button"
              onClick={link.onClick}
              className={startSidebarLink}
            >
              <span className="shrink-0 w-10 h-10 rounded-lg bg-red-50 dark:bg-red-950/40 flex items-center justify-center text-xl leading-none">
                <span aria-hidden="true">{link.emoji}</span>
              </span>
              <span className="min-w-0">
                <span className="block font-semibold text-base text-gray-800 dark:text-white">{link.title}</span>
                {link.subtitle && (
                  <span className="block text-xs text-gray-500 dark:text-gray-400 truncate">
                    {link.subtitle}
                  </span>
                )}
              </span>
            </button>
          ))}
        </aside>
      </div>

      <StartQuestionModal
        isOpen={showQuestionModal}
        onClose={() => {
          setShowQuestionModal(false);
          setQuestionText('');
        }}
        onSubmit={handleQuestionSubmit}
        questionText={questionText}
        setQuestionText={setQuestionText}
        assigneeName={questionAssignee?.name || successManager.name}
        loading={questionModalLoading}
        submitting={isSubmittingQuestion}
      />

      </div>
    </div>
  );
};

export default StartDashboard;
